import { RefObject } from 'react'
import type { NumberFormatValues } from 'react-number-format/types/types'

import { getRoles } from './auth'
import { AxiosError, AxiosResponse } from 'axios'
import { TFunction } from 'i18next'
import { IGrowthRate } from 'interfaces/redux/goal'
import moment from 'moment'
import {
  GET_BRANCH,
  GET_BRANCH_UPLOAD,
  GET_CATEGORY,
  GET_SCOPE,
  SET_LOADER,
} from 'redux/action/type'
import { store } from 'redux/store/store'
import { defaultOption } from 'utils/constants/branch'
import {
  COLORS,
  CONVERT_TO_TCO2,
  YEAR_FINANCIAL_START,
} from 'utils/constants/common'
import {
  ANALYSIS_TYPE_SCOPE1,
  ANALYSIS_TYPE_SCOPE3,
  EMISSION_UNIT,
  GOAL_VERSION_STATUS,
  INPUT_EMISSION_AMOUNT_DECIMAL_PRECISION,
  MAX_DECIMAL_DIGITS,
  ReduxProps,
  ScopeType,
  SEARCH_ANALYSIS_TYPE,
  TARGET_SELECT_YEAR_TYPE,
} from 'utils/constants/enums'
import { QUERY_PARAMS } from 'utils/constants/keyword'
import { LS_USER_INFO } from 'utils/constants/localStorage'
import { ERROR_DETAIL_CODE_UPLOAD_CSV } from 'utils/constants/statusCode'
import { MAX_LENGTH } from 'utils/constants/validation'
import i18n from 'utils/lang'
import { bigDivide } from 'utils/plugins/big-config'

import { apiHelper } from '.'

export const getListScope = async () => {
  store.dispatch({
    type: SET_LOADER,
    data: true,
  })

  try {
    const res = await apiHelper.getSystemSettingScope()
    store.dispatch({
      type: GET_SCOPE,
      data: res.data.data,
    })

    if (res.data.data) {
      res.data.data.map((item: any) => {
        if (item.scopeType === ScopeType.SCOPE3) {
          store.dispatch({
            type: GET_CATEGORY,
            data: item.category,
          })
        }
      })
    }
  } catch (err) {
    if (!(err instanceof Error)) {
      const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
      console.error(res.data)
    }
  } finally {
    store.dispatch({
      type: SET_LOADER,
      data: false,
    })
  }
}

export const generateRandomString = () => {
  let result = ''
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  for (let i = 0; i < 6; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length))
  }
  return result
}

export const formatValueNumber = (value: number) => {
  if (value > 999) {
    return new Intl.NumberFormat('ja-JP').format(value)
  }
  return value
}

export const formatNumber = (
  value: number,
  maximum: number = MAX_DECIMAL_DIGITS._4,
) =>
  new Intl.NumberFormat('ja-JP', {
    maximumFractionDigits: maximum,
  }).format(value)

export const trimAndToUpperCase = (text: string) => text.trim().toUpperCase()

export const generateFullName = (
  firstName: string = '',
  lastName: string = '',
) => `${firstName || ''} ${lastName || ''}`.trim()

export const getOptionFromValue = (options: IOption[], value: string) =>
  options.find((option) => option.value === value)

export const convertKey = (
  t: TFunction<'translation', undefined, 'translation'>,
  items: IOption[],
) =>
  items.map(({ label, value }) => ({
    label: t(label),
    value,
  }))

export const getViewerBranchTitle = () => {
  const userInfo = JSON.parse(localStorage.getItem(LS_USER_INFO) ?? '')
  const branchInfo = userInfo.branch
  const division = branchInfo?.division
  const company = division?.company
  const country = company?.country

  const title = `${country?.countryName[i18n.language]} - ${company
    ?.companyName[i18n.language]} - ${division?.divisionName[
    i18n.language
  ]} - ${branchInfo?.branchName[i18n.language]}`

  return title
}

export const getFacilityFromLocalStorage = () => {
  let country = defaultOption
  let company = defaultOption
  let division = defaultOption
  let branch = defaultOption

  const { isFacilityManager, isDataRegister, isFacilityViewer } = getRoles()
  if (isFacilityManager || isDataRegister || isFacilityViewer) {
    const userInfo = JSON.parse(localStorage.getItem(LS_USER_INFO) ?? 'null')
    const branchInfo = userInfo.branch
    country = {
      value: branchInfo.division.company.country.id || defaultOption.value,
      label:
        branchInfo.division.company.country.countryName.jp ||
        defaultOption.label,
    }
    company = {
      value: branchInfo.division.company.id || defaultOption.value,
      label: branchInfo.division.company.companyName.jp || defaultOption.label,
    }
    division = {
      value: branchInfo.division.id || defaultOption.value,
      label: branchInfo.division.divisionName || defaultOption.label,
    }
    branch = {
      value: branchInfo.id || defaultOption.value,
      label: branchInfo.branchName.jp || defaultOption.label,
    }
  }

  return { country, company, division, branch }
}

export const getFileNameWithoutExtension = (fileName: string): string => {
  const hasExtension: boolean = fileName.includes('.')
  if (hasExtension) {
    return fileName.substring(0, fileName.lastIndexOf('.'))
  }
  return fileName
}

export const truncateStringWithEllipsis = (
  inputString: string,
  limit: number = 40,
): string => {
  const ellipsis = '...'
  if (inputString.length > limit) {
    inputString = `${inputString.substring(0, limit)}${ellipsis}`
  }
  return inputString
}

export const findBranchDataById = (dataCountry: any, data: any) => {
  const country = dataCountry.find((item: any) => item.id === data.countryId)
  const company = country?.companies?.find(
    (item: any) => item.id === data.companyId,
  )
  const division = company?.divisions.find(
    (item: any) => item.id === data.divisionId,
  )
  const branch = division?.branches.find(
    (item: any) => item.id === data.branchId,
  )

  return {
    country,
    company,
    division,
    branch,
    countryId: data.countryId,
    companyId: data.companyId,
    divisionId: data.divisionId,
    branchId: data.branchId,
  }
}

/**
 * Build map branchId -> itemIds from Redux common (dataCountry or dataBranchUpload).
 * Used to filter scope2 items per branch in analysis branch table.
 */
export const getBranchItemIdsMapFromCommon = (
  common: any,
): Record<string, string[]> => {
  const map: Record<string, string[]> = {}
  const dataCountry =
    common?.dataCountry ?? common?.dataBranchUpload?.countries ?? []
  if (!Array.isArray(dataCountry)) return map
  dataCountry.forEach((country: any) => {
    country?.companies?.forEach((company: any) => {
      company?.divisions?.forEach((division: any) => {
        division?.branches?.forEach((branch: any) => {
          if (branch?.id && Array.isArray(branch?.items)) {
            map[branch.id] = branch.items
              .map((item: any) =>
                typeof item === 'string'
                  ? item
                  : item?.id ?? item?.itemId ?? '',
              )
              .filter(Boolean)
          }
        })
      })
    })
  })
  return map
}

export const handleParamsBranch = (
  headerBranchData: IHeaderData,
  headerBranchCompareData: IHeaderData,
  searchType: SEARCH_ANALYSIS_TYPE,
  compare: boolean,
  isDisabledFacilityAndName: boolean,
) => {
  let params
  switch (searchType) {
    case SEARCH_ANALYSIS_TYPE.COMPANY:
      params = {
        companyId: headerBranchData?.company?.value || defaultOption.value,
        branchId: headerBranchData?.branch?.value || defaultOption.value,
        name: headerBranchData.name,
      }
      if (compare) {
        params = {
          ...params,
          companyIdCompare:
            headerBranchCompareData?.company?.value || defaultOption.value,
          branchIdCompare:
            headerBranchCompareData?.branch?.value || defaultOption.value,
          nameCompare: headerBranchCompareData.name,
        }
      }
      break
    case SEARCH_ANALYSIS_TYPE.GROUP:
      params = {
        groupId: headerBranchData?.group?.value || defaultOption.value,
        name: headerBranchData.name,
      }
      if (compare) {
        params = {
          ...params,
          groupIdCompare:
            headerBranchCompareData?.group?.value || defaultOption.value,
          nameCompare: headerBranchCompareData.name,
        }
      }
      break
  }
  return {
    ...params,
    ...(isDisabledFacilityAndName && {
      branchId: '',
      name: '',
      companyIdCompare: '',
      branchIdCompare: '',
      nameCompare: '',
    }),
  }
}

export const getLabelCompare = (
  headerBranchData: IHeaderData,
  headerBranchCompareData: IHeaderData,
  searchType: SEARCH_ANALYSIS_TYPE,
): { first: string; second: string } => {
  switch (searchType) {
    case SEARCH_ANALYSIS_TYPE.COMPANY:
      return {
        first: headerBranchData?.branch?.value
          ? headerBranchData?.branch?.label
          : headerBranchData?.company?.label,

        second: headerBranchCompareData?.branch?.value
          ? headerBranchCompareData?.branch?.label
          : headerBranchCompareData?.company?.label,
      }
    case SEARCH_ANALYSIS_TYPE.GROUP:
      return {
        first: headerBranchData?.group?.label || '-',
        second: headerBranchCompareData?.group?.label || '-',
      }
  }
}

const getReduxType = (value: string) => {
  switch (value) {
    case ReduxProps.DATA_BRANCH:
      return GET_BRANCH
    case ReduxProps.DATA_BRANCH_UPLOAD:
      return GET_BRANCH_UPLOAD
  }
}

export const setDataBranch = (type: string, data: any) => ({
  type: getReduxType(type),
  data,
})

export const isOverflown = (element: RefObject<any>) =>
  element.current?.scrollHeight > element.current?.clientHeight ||
  element.current?.scrollWidth > element.current?.clientWidth

export const convertListToOption = ({
  list,
  key = 'id',
  value,
}: {
  list: any
  key?: string
  value: string
}) => {
  const options: IOption[] = []
  list?.map((item: any) => {
    options.push({
      value: item?.[key],
      label: (item?.[value] as any)?.[i18n.language],
    })
  })

  return options
}

export const hasUnreadNotification = (notifications: any) =>
  notifications.some((notification: any) => notification.isUnread)

export const getLocalStorage = (key: string) => {
  const item = localStorage.getItem(key)
  if (!item) {
    return undefined
  }

  try {
    return JSON.parse(item)
  } catch (e) {
    console.error(e)
    return undefined
  }
}

export const getFirstDayOfYear = () => {
  const now = new Date()
  const firstDayOfYear = new Date(now.getFullYear(), 0, 1)
  return firstDayOfYear
}

export const scopeOptions = (
  t: TFunction<'translation', undefined, 'translation'>,
  scopeUsed?: number[],
) => [
  {
    value: String(ScopeType.SCOPE1),
    label: t<string>('analysis.scope1.direct_emissions_1'),
    isDisabled: scopeUsed?.includes(ScopeType.SCOPE1),
  },
  {
    value: String(ScopeType.SCOPE2),
    label: t<string>('analysis.scope1.indirect_emissions_2'),
    isDisabled: scopeUsed?.includes(ScopeType.SCOPE2),
  },
  {
    value: String(ScopeType.SCOPE3),
    label: t<string>('analysis.scope1.other_indirect_emissions'),
    isDisabled: scopeUsed?.includes(ScopeType.SCOPE3),
  },
]

export const getFinancialYearMonths = (
  financialMonth: number,
  year: string,
  onlyMonth?: boolean,
): string[] => {
  const months: string[] = []
  let startDate = moment(`${year}-${financialMonth}`, 'YYYY-M')

  for (let i = 0; i < 12; i++) {
    months.push(onlyMonth ? startDate.format('M') : startDate.format('YYYY/MM'))
    startDate = startDate.add(1, 'month')
  }

  return months
}

export const renderMonthFinancialYear = (
  financialMonth: number,
  year: string,
  isMappingKey: boolean = false,
) => {
  const month = getFinancialYearMonths(financialMonth, year).map(
    (label, index) => {
      if (isMappingKey) {
        const [, currentMonth] = label.split('/')
        return {
          key: `${Number(currentMonth)}`,
          label: `${Number(currentMonth)}`,
          _style: { width: '100px' },
        }
      }
      return {
        key: `${+index}`,
        label,
        _style: { width: '100px' },
      }
    },
  )

  return month
}

/**
 * Sorts an array of ICategory objects by the category name in Japanese.
 * @param {ICategory[]} arr - The array of ICategory objects to be sorted.
 * @returns {ICategory[]} - The sorted array of ICategory objects.
 */
export const sortArrayByCategoryName = (arr: ICategory[]): ICategory[] => {
  if (!Array.isArray(arr)) return []

  return arr.sort((a, b) => {
    // Handle cases where categoryName or jp is undefined
    if (!a.categoryName || !a.categoryName.jp) return 1
    if (!b.categoryName || !b.categoryName.jp) return -1

    const nameA = a.categoryName.jp
    const nameB = b.categoryName.jp

    // Parse the numeric part at the beginning of the string
    const numA = parseInt(nameA.split('.')[0], 10)
    const numB = parseInt(nameB.split('.')[0], 10)

    // Handle cases where parsing fails and returns NaN
    if (Number.isNaN(numA)) return 1
    if (Number.isNaN(numB)) return -1

    return numA - numB
  })
}

/**
 * Get datasets for branch chart.
 *
 * @param {Object} params - The parameters.
 * @param {IChartBranchData} params.data - The chart data for each scope.
 * @param {TFunction} params.t - The translation function.
 * @returns {IChartDataset[]} The datasets for the chart.
 */
export const getDatasetsBranchChart = ({
  data,
  t,
}: {
  data: IChartBranchData
  t: TFunction
}): IChartDataset[] => {
  const SCOPES = [ScopeType.SCOPE1, ScopeType.SCOPE2, ScopeType.SCOPE3]
  const getDataByScope = (scope: ScopeType): number[] => {
    switch (scope) {
      case ScopeType.SCOPE1:
        return data.scope1
      case ScopeType.SCOPE2:
        return data.scope2
      case ScopeType.SCOPE3:
        return data.scope3
    }
  }

  const createDataset = (scope: number): IChartDataset => ({
    backgroundColor: COLORS?.[`CHART_SCOPE_${scope}` as keyof typeof COLORS],
    data: getDataByScope(scope),
    label: t(`nav.scope${scope}`),
    barPercentage: 0.5,
  })

  return SCOPES.map((scope) => createDataset(scope))
}

/**
 * Returns a Date object representing January 1st of a given year, possibly increased by a specified number of years.
 *
 * @param year - The base year. If null or undefined, the function returns undefined.
 * @param increase - The number of years to add to the base year. If not a valid number, no years are added.
 * @returns A Date object representing January 1st of the calculated year, or undefined if the year is null or undefined.
 */
export const getTimeOfYear = (
  year?: number | null,
  increase?: number,
): Date | undefined => {
  if (year == null) return undefined
  const calculatedYear = isValidNumber(increase) ? year + increase! : year
  return new Date(calculatedYear, 0, 1)
}

export const getYearFromDate = (date?: Date): number | undefined => {
  if (!date) return undefined
  const momentDate = moment(date)
  return momentDate.year()
}

export function isValidNumber(value: any) {
  if (value === null || value === undefined) {
    return false
  }

  if (typeof value === 'number') {
    return !Number.isNaN(value) && Number.isFinite(value)
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim()

    if (trimmedValue === '') {
      return false
    }

    const number = Number(trimmedValue)
    return !Number.isNaN(number) && Number.isFinite(number)
  }

  return false
}

/**
 * Get a valid value from the ScopeType enum.
 *
 * @param {number | string | null} value - The value to check.
 * @returns {ScopeType | undefined} - The valid ScopeType value or undefined if invalid.
 */
export const getScopeType = (
  value: number | string | null,
): ScopeType | undefined => {
  const num = Number(value)
  if (!isValidNumber(num)) return undefined
  return [ScopeType.SCOPE1, ScopeType.SCOPE2, ScopeType.SCOPE3].includes(
    num as ScopeType,
  )
    ? (num as ScopeType)
    : undefined
}

/**
 * Validates if the input number value is within the specified maximum limit.
 *
 * @param {NumberFormatValues} values - The values object containing the number to validate.
 * @param {number} [max=MAX_LENGTH.NUMBER_MAX_LIMIT] - The maximum allowable limit for the number.
 * @returns {boolean} - Returns true if the number is within the maximum limit or if the number is undefined/null, otherwise false.
 */
export const validateMaxInputNumberValue = (
  values: NumberFormatValues,
  max: number = MAX_LENGTH.NUMBER_MAX_LIMIT,
): boolean => {
  const { floatValue } = values
  if (!floatValue) return true
  return Math.floor(floatValue) <= max
}

export const handleIsAllowedMaxInputNumberValue =
  (
    max: number = MAX_LENGTH.NUMBER_MAX_LIMIT,
  ): ((values: NumberFormatValues) => boolean) =>
  (values: NumberFormatValues): boolean => {
    const { floatValue } = values
    if (!floatValue) return true
    return Math.floor(floatValue) <= max
  }

export const generateYearOptions = () => {
  const optionsYear = []
  const date = new Date()
  for (let i = date.getFullYear(); i >= YEAR_FINANCIAL_START; i--) {
    optionsYear.push({
      label: i.toString(),
      value: i.toString(),
    })
  }
  return optionsYear
}
export const generateVersionStatusOptions = (
  t: TFunction,
  includeAllOption = false,
): IOption[] => {
  const statusOptions: IOption[] = [
    ...(includeAllOption
      ? [{ label: t('base.option_default.all'), value: '' }]
      : []),
    {
      label: t('goal.version_status.preparing'),
      value: String(GOAL_VERSION_STATUS.PREPARING),
    },
    {
      label: t('goal.version_status.in_progress'),
      value: String(GOAL_VERSION_STATUS.IN_PROGRESS),
    },
    {
      label: t('goal.version_status.completed'),
      value: String(GOAL_VERSION_STATUS.COMPLETED),
    },
  ]

  return statusOptions
}

/**
 * Truncates a string to a specified length and appends "..." at the end if it exceeds that length.
 * If the input is not a string, returns the input as is.
 *
 * @param {any} input - The input value to be truncated.
 * @param {number} [length=15] - The number of characters to display (default is 15).
 * @returns {any} - The truncated string with "..." appended or the input value if it's not a string.
 */
export const truncateString = (input: any, length: number = 15): any => {
  if (typeof input !== 'string') {
    return input
  }

  if (input.length <= length) {
    return input
  }

  return `${input.substring(0, length)}...`
}

/**
 * Gets the goal version status based on the provided status code.
 *
 * @param {number} status - The status code of the goal version.
 * @param {TFunction} t - The translation function.
 * @returns {string} - The translated status string.
 */
export const getGoalVersionStatus = (status: number, t: TFunction): string => {
  switch (status) {
    case GOAL_VERSION_STATUS.COMPLETED:
      return t('goal.version_status.completed')
    case GOAL_VERSION_STATUS.IN_PROGRESS:
      return t('goal.version_status.in_progress')
    case GOAL_VERSION_STATUS.PREPARING:
      return t('goal.version_status.preparing')
    default:
      return ''
  }
}

export const isGreaterThanThreshold = (
  value: any,
  threshold: number,
): boolean => isValidNumber(value) && value > threshold

/**
 * Generates default values for the specified year range with a default rate of 100.
 * Optionally updates existing rates from oldValue for overlapping years.
 * @param startYear - The starting year of the range.
 * @param endYear - The ending year of the range.
 * @param oldValue - Optional old values to update existing rates.
 * @returns An array of objects with year and rate properties, or an empty array if startYear or endYear is undefined.
 */
export const generateGrowthRateValues = (
  startYear?: number,
  endYear?: number,
  oldValue?: IGrowthRate[],
): IGrowthRate[] => {
  if (startYear === undefined || endYear === undefined) {
    return []
  }

  const defaultValues = Array.from(
    { length: endYear - startYear + 1 },
    (_, index) => ({
      year: startYear + index,
      rate: 100,
    }),
  )

  if (oldValue) {
    // Update rates from oldValue for overlapping years
    const updatedValues = defaultValues.map((defaultValue) => {
      const matchedOldValue = oldValue.find(
        (old) => old.year === defaultValue.year,
      )
      if (matchedOldValue) {
        return { year: defaultValue.year, rate: matchedOldValue.rate }
      }
      return defaultValue
    })
    return updatedValues
  }

  return defaultValues
}

/**
 * Generates a list of month options with labels and values.
 * @returns An array of objects containing label and value for each month option.
 */
export const listOptionMonth = (): { label: string; value: string }[] => {
  const result = []
  for (let i = 1; i <= 12; i++) {
    result.push({ label: i.toString(), value: i.toString() })
  }
  return result
}

/**
 * Formats data with year/month using moment.js, incrementing the year if the month exceeds 12.
 *
 * @param {Array<Object>} data - The array of data objects to be formatted.
 * Each object should have a `month` property representing the month number (1-12).
 * @param {number | string} startYear - The starting year for the calculation.
 * This represents the base year from which the months will be calculated.
 * @returns {Array<Object>} - A new array of data objects with the `month` property formatted as `yyyy/mm`.
 * If `data` is not an array, an empty array is returned.
 */
export const formatDataWithYearMonth = (
  data: any[],
  startYear: number | string,
): any[] => {
  // Check if the input data is an array
  if (!Array.isArray(data) || !startYear) return []

  // Convert startYear to a number
  let year = Number(startYear)

  return data.map((item) => {
    // Validate the month property
    if (!item?.month || !isValidNumber(item.month)) return item

    // Calculate the correct year and month
    const month = item.month - 1
    const date = moment().year(year).month(month)
    if (month >= 11) {
      year += 1
    }
    // Format the date as yyyy/mm
    const formattedMonth = date.format('YYYY/MM')

    // Return a new object with the formatted month and original data
    return {
      ...item,
      month: formattedMonth,
    }
  })
}

/**
 * Generates a postfix for the document title based on the target type and date range.
 * @param {TARGET_SELECT_YEAR_TYPE} targetType - The type of target selection.
 * @param {string} [year=''] - The selected year (used when targetType is YEAR).
 * @param {string} [dateFrom=''] - The start date (used when targetType is MONTH_YEAR).
 * @param {string} [dateTo=''] - The end date (used when targetType is MONTH_YEAR).
 * @returns {string} The generated postfix for the document title.
 */
export const getPostfixDocumentTitle = (
  targetType: TARGET_SELECT_YEAR_TYPE,
  year: string = '',
  dateFrom: string = '',
  dateTo: string = '',
): string => {
  const postfixes = {
    [TARGET_SELECT_YEAR_TYPE.YEAR]: year,
    [TARGET_SELECT_YEAR_TYPE.MONTH_YEAR]: `${dateFrom}-${dateTo}`,
  }

  return postfixes[targetType] || ''
}

/**
 * Round down a number to a specified number of decimal places
 * @param {number} value The number to round down
 *th @param {number} [numberOfDecimal=0] The number of decimal places to keep (default is 0)
 * @returns The rounded down number
 */
export const roundDownDecimalNumber = (
  value: number,
  numberOfDecimal: number = 0,
): number =>
  Math.round(value * 10 ** numberOfDecimal) / 10 ** numberOfDecimal || 0

/**
 * Returns a color from the provided colors array based on the given index.
 * If the index exceeds the length of the colors array, it wraps around using modulus.
 *
 * @param {number} index - The index to get the corresponding color.
 * @param {string[]} colors - An array of color strings.
 * @returns {string} - The color corresponding to the given index.
 */
export const getColor = (index: number, colors: string[]): string =>
  colors[index % colors.length]

/**
 * Generates an array of colors based on the number of elements.
 * Uses the getColor function to retrieve the corresponding color for each element.
 *
 * @param {number} count - The number of elements in the array.
 * @param {string[]} colors - An array of available color strings.
 * @returns {string[]} - An array of colors corresponding to the number of elements.
 */
export const generateColorArray = (
  count: number = 0,
  colors: string[],
): string[] => {
  const colorArray: string[] = []
  for (let i = 0; i < count; i++) {
    colorArray.push(getColor(i, colors))
  }
  return colorArray
}
export const renderColumnsFromTo = (range: string[]) =>
  range.map((date, index) => ({
    key: `${+index}`,
    label: date,
    _style: { width: '100px', textAlign: 'right' },
  }))

export const getPropertyDataEnergy = (
  type: string = '',
  scope: ScopeType = ScopeType.SCOPE1,
) => {
  if (type === ANALYSIS_TYPE_SCOPE1.GASOLINE && scope === ScopeType.SCOPE1) {
    return 'fuelGasOlineUsage'
  }
  if (type === ANALYSIS_TYPE_SCOPE1.CITY_GAS && scope === ScopeType.SCOPE1) {
    return 'fuelCityGasUsage'
  }
  if (type === ANALYSIS_TYPE_SCOPE1.DIESEL && scope === ScopeType.SCOPE1) {
    return 'fuelDieselUsage'
  }
  if (type === ANALYSIS_TYPE_SCOPE3.TAP_WATER && scope === ScopeType.SCOPE3) {
    return 'tapWater'
  }

  if (type === ANALYSIS_TYPE_SCOPE3.PAPER && scope === ScopeType.SCOPE3) {
    return 'paper'
  }

  return ''
}

export const getUnitDataEnergy = (
  type: string = '',
  scope: ScopeType = ScopeType.SCOPE1,
) => {
  if (type === ANALYSIS_TYPE_SCOPE1.GASOLINE && scope === ScopeType.SCOPE1) {
    return EMISSION_UNIT.LITRE
  }
  if (type === ANALYSIS_TYPE_SCOPE1.CITY_GAS && scope === ScopeType.SCOPE1) {
    return EMISSION_UNIT.CUBIC_METER
  }
  if (type === ANALYSIS_TYPE_SCOPE1.DIESEL && scope === ScopeType.SCOPE1) {
    return EMISSION_UNIT.LITRE
  }
  if (type === ANALYSIS_TYPE_SCOPE3.TAP_WATER && scope === ScopeType.SCOPE3) {
    return EMISSION_UNIT.CUBIC_METER
  }

  return ''
}

export const getValueCompare = (
  emissionName: string,
  branch2Compare: any,
  itemId: string = '',
) =>
  branch2Compare.find(
    (e: any) =>
      e?.emissionName[i18n.language].trim() === emissionName.trim() &&
      e.itemId === itemId,
  )

export const getLinkToRegistrationData = ({
  emissionName = '',
  itemName = '',
  categoryName = '',
  dateFrom,
  dateTo,
  tab,
  headerBranchData,
}: {
  emissionName: string
  itemName: string
  categoryName: string
  dateFrom: string
  dateTo: string
  tab: ScopeType
  headerBranchData?: IHeaderData
}) => {
  const searchParams = new URLSearchParams()
  searchParams.set(QUERY_PARAMS.TAB, `${tab}`)
  searchParams.set(QUERY_PARAMS.YEAR_MONTH_USE_FROM, dateFrom)
  searchParams.set(QUERY_PARAMS.YEAR_MONTH_USE_TO, dateTo)
  emissionName && searchParams.set(QUERY_PARAMS.EMISSION_NAME, emissionName)
  itemName && searchParams.set(QUERY_PARAMS.ITEM_NAME, itemName)
  categoryName && searchParams.set(QUERY_PARAMS.CATEGORY_NAME, categoryName)

  const userInfo = getLocalStorage(LS_USER_INFO)
  const companyId = userInfo?.company?.id
  const isShare =
    headerBranchData?.group?.value ||
    (headerBranchData?.company?.value &&
      companyId !== headerBranchData?.company?.value)

  const path = isShare ? '/linkage/data' : '/upload/list'
  if (headerBranchData?.group?.value) {
    searchParams.set(QUERY_PARAMS.GROUP_ID, headerBranchData?.group?.value)
    searchParams.set(QUERY_PARAMS.GROUP_NAME, headerBranchData?.group?.label)
  }

  return `${path}?${searchParams.toString()}`
}

/**
 * Interface for the configuration object used in the decimal validator.
 */
interface DecimalValidatorOptions {
  maxIntegerDigits?: number // Maximum allowed number of integer digits
  maxFractionalDigits?: number // Maximum allowed number of fractional digits
}

/**
 * Creates a decimal validator function.
 * The validator checks if the given value meets the specified maximum integer and fractional digit limits.
 *
 * @param {DecimalValidatorOptions} options - An object containing the maximum digit limits.
 * @returns {function} A validation function that checks the value against the digit limits.
 */
export function createDecimalValidator(
  options: DecimalValidatorOptions = {},
): (values: Pick<NumberFormatValues, 'value'>) => boolean {
  const maxIntegerDigits =
    options.maxIntegerDigits ??
    INPUT_EMISSION_AMOUNT_DECIMAL_PRECISION.MAX_INTEGER_DIGITS // Default to 10 if not provided
  const maxFractionalDigits =
    options.maxFractionalDigits ??
    INPUT_EMISSION_AMOUNT_DECIMAL_PRECISION.MAX_DECIMAL_DIGITS // Default to 20 if not provided

  return function validate(values: Pick<NumberFormatValues, 'value'>): boolean {
    // If value is undefined or an empty string, return true
    if (values.value === undefined || values.value === '') {
      return true
    }

    // Split the value into integer and fractional parts
    const [integerPart, decimalPart] = values.value.split('.')

    // Check the length of the integer and fractional parts
    const isValidIntegerPart = integerPart.length <= maxIntegerDigits
    const isValidDecimalPart = decimalPart
      ? decimalPart.length <= maxFractionalDigits
      : true

    // Return true if both parts are valid
    return isValidIntegerPart && isValidDecimalPart
  }
}

/**
 * Format decimal number by:
 * - Adding thousand separators to integer part
 * - Removing trailing zeros after decimal point
 * - Handling invalid input
 * @param value - Decimal number from MySQL (string or number)
 * @param decimals - toFixed number ( number)
 * @returns Formatted string or original value if invalid
 */
export function formatDecimalNumber(
  value: string | number,
  decimals?: number,
): string {
  // Return original value if undefined or null
  if (value === undefined || value === null) {
    return ''
  }

  // Convert to string
  const stringValue = value.toString()

  // Check if value is a valid number
  if (!/^-?\d*\.?\d+$/.test(stringValue)) {
    return stringValue
  }

  // Split number into integer and decimal parts
  const [integerPart, decimalPart = ''] = stringValue.split('.')

  // Format integer part with thousand separators
  const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  // If decimals is not provided, keep all decimal places
  if (decimals == null) {
    // Remove trailing zeros and return if there are remaining decimal digits
    const trimmedDecimalPart = decimalPart.replace(/0+$/, '')
    return trimmedDecimalPart.length > 0
      ? `${formattedIntegerPart}.${trimmedDecimalPart}`
      : formattedIntegerPart
  }

  // Handle decimal part rounding
  let roundedDecimalPart = decimalPart
  if (decimalPart.length > decimals) {
    // Get digits we need to check for rounding
    const checkDigit = parseInt(decimalPart[decimals], 10)
    roundedDecimalPart = decimalPart.slice(0, decimals)

    // Round up if necessary
    if (checkDigit >= 5) {
      const decimalAsNum = parseInt(roundedDecimalPart, 10) + 1
      roundedDecimalPart = decimalAsNum.toString().padStart(decimals, '0')

      // Handle carrying over to integer part if all 9's
      if (roundedDecimalPart.length > decimals) {
        const integerAsNum = parseInt(integerPart, 10) + 1
        return formatDecimalNumber(integerAsNum.toString(), decimals)
      }
    }
  }

  // Remove trailing zeros
  const trimmedDecimalPart = roundedDecimalPart.replace(/0+$/, '')

  // Return combined result
  return trimmedDecimalPart.length > 0
    ? `${formattedIntegerPart}.${trimmedDecimalPart}`
    : formattedIntegerPart
}

export function convertToTco2(
  value: number | string = 0,
  factorMainUnit: string,
) {
  const unit = factorMainUnit?.split('/')[0]
  if (CONVERT_TO_TCO2[unit] && isValidNumber(value)) {
    return bigDivide(value, CONVERT_TO_TCO2[unit]).toString()
  }
  return typeof value === 'string' ? parseFloat(value) : value
}

/**
 * Returns a formatted string representing a fiscal year period
 * @param year The fiscal year
 * @param fiscalMonth The starting month of the fiscal year (1-12)
 * @returns Formatted string like "2023(2023/04~2024/03)"
 */
export const getFinancialYearPeriod = (
  year: string | number,
  fiscalMonth: string | number,
  isPeriodOnly: boolean = false,
): string => {
  if (!isValidNumber(year) || !isValidNumber(fiscalMonth)) {
    return ''
  }

  const yearNum = Number(year)
  const monthNum = Number(fiscalMonth)
  const isSepToDecMonths = monthNum >= 9 && monthNum <= 12

  const start = moment(`${yearNum}-${monthNum}`, 'YYYY-M')
  const end = moment(start).add(11, 'months')
  let output = `（${start.format('YYYY/MM')}~${end.format('YYYY/MM')}）`
  if (!isPeriodOnly)
    output = `${isSepToDecMonths ? yearNum + 1 : yearNum}${output}`

  return output
}

/**
 * Returns the financial year based on the given year and fiscal month
 * @param year The base year
 * @param fiscalMonth The starting month of the fiscal year (1-12)
 * @returns The financial year number
 */
export const getFinancialYear = (
  year: string | number,
  fiscalMonth: string | number,
): number | null => {
  if (!isValidNumber(year) || !isValidNumber(fiscalMonth)) {
    return null
  }

  const yearNum = Number(year)
  const monthNum = Number(fiscalMonth)
  const isSepToDecMonths = monthNum >= 9 && monthNum <= 12

  return isSepToDecMonths ? yearNum + 1 : yearNum
}

export function convertEmissionKeys(obj: object) {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      let newKey = key.replace(/^emission/, '')

      newKey = newKey.charAt(0).toLowerCase() + newKey.slice(1)

      return [newKey, value]
    }),
  )
}

export const convertErrorMessage = (
  errorObject: any,
  field: string,
  t: TFunction<'translation', undefined, 'translation'>,
) => {
  let error = ''
  switch (true) {
    case errorObject?.code === ERROR_DETAIL_CODE_UPLOAD_CSV.FIELD_IS_EMPTY:
      error = t('message.require')
      break
    case errorObject?.code === ERROR_DETAIL_CODE_UPLOAD_CSV.FIELD_INVALID &&
      field === 'usageDate':
      error = t('csv_input.errors.usage_date_not_valid')
      break
    case errorObject?.code === ERROR_DETAIL_CODE_UPLOAD_CSV.FIELD_INVALID &&
      field === 'amount':
      error = t('csv_input.errors.amount_not_valid')
      break
    case errorObject?.code === ERROR_DETAIL_CODE_UPLOAD_CSV.FIELD_INVALID &&
      field === 'branch':
      error = t('csv_input.errors.branch_not_valid')
      break

    case errorObject?.code === ERROR_DETAIL_CODE_UPLOAD_CSV.FIELD_INVALID &&
      field === 'classification':
      error = t('csv_input.errors.classification_not_valid')
      break

    case errorObject?.code === ERROR_DETAIL_CODE_UPLOAD_CSV.FIELD_INVALID &&
      errorObject?.type ===
        ERROR_DETAIL_CODE_UPLOAD_CSV.BRANCH_ITEM_NOT_FOUND &&
      field === 'item':
      error = t('csv_input.errors.company_electric_not_exist')
      break

    case errorObject?.code === ERROR_DETAIL_CODE_UPLOAD_CSV.FIELD_INVALID &&
      field === 'item':
      error = t('csv_input.errors.item_not_valid')
      break
    case errorObject?.code === ERROR_DETAIL_CODE_UPLOAD_CSV.FIELD_INVALID &&
      field === 'unit':
      error = t('csv_input.errors.unit_not_valid')
      break
    case errorObject?.code === ERROR_DETAIL_CODE_UPLOAD_CSV.FIELD_INVALID &&
      field === 'category':
      error = t('csv_input.errors.category_not_valid')
      break
    case errorObject?.code === ERROR_DETAIL_CODE_UPLOAD_CSV.FIELD_INVALID:
      error = t('csv_input.errors.emission_not_valid')
      break
  }
  return error
}

/**
 * Calculates financial year period based on the financial month and current date
 *
 * @param {number} financialMonth - The financial month (1-12)
 * @returns {Object} Object containing start and end dates of the financial year
 */
export const getFinancialYearFromTo = (
  financialMonth: number,
): { from: Date; to: Date } => {
  const today = new Date()
  const currentMonth = today.getMonth() + 1
  const currentYear = today.getFullYear()

  let startYear = currentYear

  // If current month is before the financial month, then we're in the previous financial year
  if (currentMonth < financialMonth) {
    startYear = currentYear - 1
  }

  const from = new Date(startYear, financialMonth - 1, 1)

  const to = new Date(startYear + 1, financialMonth - 1, 0)

  return { from, to }
}

/**
 * Parses a date string into a Date object
 *
 * @param {string | null} dateStr - The date string to parse
 * @returns {Date | null} A Date object if the string is valid, null otherwise
 */
export const parseDateString = (dateStr: string | null): Date | null => {
  if (!dateStr) return null

  try {
    const date = new Date(dateStr)
    // Check if date is valid
    if (!Number.isNaN(date.getTime())) {
      return date
    }
  } catch (error) {
    return null
  }
  return null
}

/**
 * 入力テーブルの行IDを採番する。
 *
 * 従来は `Date.now() + Math.floor(Math.random() * 100) + 1` を使っていたが、
 * 100ms 以内に続けて行を追加/複製すると乱数次第でIDが衝突し、
 * 片方の行を編集・削除すると双子の行にも波及していた
 * (行の更新・削除・複製はすべて `row.id === id` で対象を特定しているため)。
 * 単調増加させることで衝突を構造的に無くす。
 */
let lastRowId = 0
export const generateRowId = (): number => {
  const now = Date.now()
  lastRowId = now > lastRowId ? now : lastRowId + 1
  return lastRowId
}
